/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Fragment, useCallback, useMemo, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import { LinkIconButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import Image from '@atlaskit/image';
import { type ExternalImageIdentifier, type MediaClientConfig } from '@atlaskit/media-client';
import { MediaViewer } from '@atlaskit/media-viewer';
import { Box, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { Carousel } from './carousel';

// MediaViewer requires a MediaClient instance, but for external images
// no real auth is needed — a stub is sufficient
const mediaClientConfig: MediaClientConfig = {
	authProvider: () => Promise.reject(new Error('Not required for external images')),
};

const messages = defineMessages({
	visitSource: {
		id: 'smart-knowledge-cards.images.visit-source',
		defaultMessage: 'Visit: {sourceTitle}',
		description:
			'Accessible label for the external link icon button overlaid on each image in the smart knowledge card. The placeholder {sourceTitle} is substituted with the title of the source website or page.',
	},
	viewImage: {
		id: 'smart-knowledge-cards.images.view-image',
		defaultMessage: 'View image: {imageName}',
		description: 'Label for the button that opens an image in the full-screen viewer',
	},
});

const styles = cssMap({
	image: {
		borderRadius: token('radius.large'),
		display: 'block',
		height: '150px',
		objectFit: 'cover',
		width: '100%',
	},
	imageContainer: {
		borderRadius: token('radius.large'),
		overflow: 'hidden',
		position: 'relative',
		scrollSnapAlign: 'start',
		width: '100%',
	},
	// Overlay to darken the image on hover/focus to make the overlay controls legible.
	overlayBlanket: {
		// @ts-expect-error -- `color.blanket` is a valid token but not in cssMap's strict backgroundColor union
		backgroundColor: token('color.blanket'),
		borderRadius: token('radius.large'),
		inset: token('space.0'),
		pointerEvents: 'none',
		position: 'absolute',
	},
	imageContainerFocusRing: {
		// Focus ring on the container: avoids overflow:hidden clipping and overlay dimming.
		// Scoped to the direct-child button so the LinkIconButton's own focus ring is unaffected.
		// @ts-expect-error -- cssMap types do not support combinators or type selectors inside :has()
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:has(> button:focus-visible)': {
			outlineColor: token('color.border.focused'),
			outlineOffset: token('border.width.focused'),
			outlineStyle: 'solid',
			outlineWidth: token('border.width.focused'),
		},
	},
	imageButton: {
		backgroundColor: 'transparent',
		borderRadius: token('radius.large'),
		borderStyle: 'none',
		cursor: 'pointer',
		display: 'block',
		// Suppress built-in focus ring; rendered on imageContainer instead (see imageContainerFocusRing).
		'&:focus-visible': {
			outlineStyle: 'none',
		},
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		width: '100%',
	},
	twoImagesButton: {
		flexShrink: 0,
		// Uses calc() to account for the gap between the images
		// `token('space.100')` = gap used by `Carousel`
		// @ts-expect-error – calc() with design tokens is not representable in the strict width type
		width: `calc((100% - ${token('space.100')}) / 2)`,
	},
	threeImagesButton: {
		flexShrink: 0,
		// Uses calc() to account for the gap between the images
		// `token('space.100')` = gap used by `Carousel`
		// @ts-expect-error – calc() with design tokens is not representable in the strict width type
		width: `calc((100% - 2 * ${token('space.100')}) / 3)`,
	},
	fourOrMoreImagesButton: {
		flexShrink: 0,
		// Uses calc() to account for the gap between the images
		// `token('space.100')` = gap used by `Carousel`
		// @ts-expect-error – calc() with design tokens is not representable in the strict width type
		width: `calc((100% - 3 * ${token('space.100')}) / 4)`,
	},
	overlayHidden: {
		opacity: 0,
		transitionDuration: '150ms',
		transitionProperty: 'opacity',
		transitionTimingFunction: 'ease-in-out',
		visibility: 'hidden',
	},
	overlayVisible: {
		opacity: 1,
		visibility: 'visible',
	},
	iconButtonOverlay: {
		borderRadius: token('radius.large'),
		pointerEvents: 'none',
		position: 'absolute',
		right: token('space.100'),
		top: token('space.100'),
		zIndex: 1,
		// Draw the focus ring on the wrapper to prevent it being clipped by the
		// overflow:hidden on imageContainer and dimmed by the blanket overlay.
		// @ts-expect-error -- cssMap types do not support combinators or type selectors inside :has()
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:has(:focus-visible)': {
			outlineColor: token('color.border.focused'),
			outlineOffset: token('border.width.focused'),
			outlineStyle: 'solid',
			outlineWidth: token('border.width.focused'),
		},
		// Suppress the native focus ring on the link — the wrapper ring replaces it.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& :focus-visible': {
			outlineStyle: 'none',
		},
	},
	iconButtonOverlayVisible: {
		backgroundColor: token('elevation.surface'),
		pointerEvents: 'auto',
	},
	bottomBar: {
		alignItems: 'center',
		backgroundColor: token('elevation.surface'),
		bottom: token('space.0'),
		display: 'flex',
		left: token('space.0'),
		minHeight: '32px',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		pointerEvents: 'none',
		position: 'absolute',
		right: token('space.0'),
		zIndex: 1,
	},
});

function ImageButton({
	buttonStyle,
	identifier,
	onClick,
	sourceTitle,
	sourceUrl,
}: {
	buttonStyle?: 'twoImagesButton' | 'threeImagesButton' | 'fourOrMoreImagesButton';
	identifier: ExternalImageIdentifier;
	onClick: (identifier: ExternalImageIdentifier) => void;
	sourceTitle: string;
	sourceUrl: string;
}) {
	const { formatMessage } = useIntl();
	const handleClick = useCallback(() => onClick(identifier), [identifier, onClick]);
	const [isOverlayVisible, setIsOverlayVisible] = useState(false);

	const showOverlay = useCallback(() => setIsOverlayVisible(true), []);
	const hideOverlay = useCallback(() => setIsOverlayVisible(false), []);

	const handleBlur = useCallback(
		(event: React.FocusEvent<HTMLDivElement>) => {
			if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
				return;
			}
			hideOverlay();
		},
		[hideOverlay],
	);

	return (
		<Box
			xcss={cx(
				styles.imageContainer,
				styles.imageContainerFocusRing,
				buttonStyle && styles[buttonStyle],
			)}
			onMouseEnter={showOverlay}
			onMouseLeave={hideOverlay}
			onFocus={showOverlay}
			onBlur={handleBlur}
		>
			<Pressable
				xcss={styles.imageButton}
				onClick={handleClick}
				aria-label={formatMessage(messages.viewImage, { imageName: identifier.name ?? '' })}
			>
				{/* eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides */}
				<Image alt={identifier.name} css={styles.image} src={identifier.dataURI} />
			</Pressable>
			{/* Blanket: dims the image on hover/focus. Driven by the same React state as the
			    icon button and bottom bar to keep all three visually in sync. */}
			<Box
				testId="image-overlay-blanket"
				xcss={cx(
					styles.overlayBlanket,
					styles.overlayHidden,
					isOverlayVisible && styles.overlayVisible,
				)}
			/>
			<Box
				testId="image-overlay-icon"
				xcss={cx(
					styles.iconButtonOverlay,
					styles.overlayHidden,
					isOverlayVisible && styles.overlayVisible,
					isOverlayVisible && styles.iconButtonOverlayVisible,
				)}
			>
				<LinkIconButton
					href={sourceUrl}
					icon={LinkExternalIcon}
					isTooltipDisabled={false}
					label={formatMessage(messages.visitSource, { sourceTitle })}
					rel="noopener noreferrer"
					spacing="default"
					target="_blank"
				/>
			</Box>
			{/* sourceTitle duplicates the LinkIconButton label — hidden from screen readers */}
			<Box
				aria-hidden="true"
				testId="image-overlay-bottom-bar"
				xcss={cx(styles.bottomBar, styles.overlayHidden, isOverlayVisible && styles.overlayVisible)}
			>
				<Text color="color.text.subtle" maxLines={1} size="small">
					{sourceTitle}
				</Text>
			</Box>
		</Box>
	);
}

export const Images = function Images({
	heading,
	images,
	maxVisibleCount,
}: {
	heading?: string;
	images: Array<{
		altText: string;
		imageUrl: string;
		sourceTitle: string;
		sourceUrl: string;
	}>;
	maxVisibleCount?: 1 | 2 | 3 | 4;
}): JSX.Element | null {
	const [selectedItem, setSelectedItem] = useState<ExternalImageIdentifier | null>(null);
	const triggerRef = useRef<Element | null>(null);

	const identifiers: ExternalImageIdentifier[] = useMemo(
		() =>
			images.map((result) => ({
				mediaItemType: 'external-image',
				dataURI: result.imageUrl,
				name: result.altText,
			})),
		[images],
	);

	const openViewer = useCallback((identifier: ExternalImageIdentifier) => {
		triggerRef.current = getDocument()?.activeElement ?? null;
		setSelectedItem(identifier);
	}, []);

	const closeViewer = useCallback(() => {
		const trigger = triggerRef.current;
		setSelectedItem(null);
		// Restore focus after React re-renders and the MediaViewer unmounts
		requestAnimationFrame(() => {
			if (trigger instanceof HTMLElement) {
				trigger.focus();
			}
		});
	}, []);

	if (images.length === 0) {
		return null;
	}

	const isSingleImage = images.length === 1;

	const buttonStyleByCountMap: Record<
		number,
		'twoImagesButton' | 'threeImagesButton' | 'fourOrMoreImagesButton'
	> = {
		2: 'twoImagesButton',
		3: 'threeImagesButton',
	};
	const effectiveVisibleCount = Math.min(maxVisibleCount ?? images.length, images.length);
	const buttonStyle = isSingleImage
		? undefined
		: (buttonStyleByCountMap[effectiveVisibleCount] ?? 'fourOrMoreImagesButton');

	const imageButtons = identifiers.map((identifier, index) => (
		<ImageButton
			key={`${identifier.dataURI}-${index}`}
			buttonStyle={buttonStyle}
			identifier={identifier}
			onClick={openViewer}
			sourceTitle={images[index].sourceTitle}
			sourceUrl={images[index].sourceUrl}
		/>
	));

	const viewer = selectedItem && (
		<MediaViewer
			mediaClientConfig={mediaClientConfig}
			selectedItem={selectedItem}
			items={identifiers}
			collectionName=""
			onClose={closeViewer}
		/>
	);

	if (isSingleImage) {
		return (
			<Stack space="space.100">
				{heading ? (
					<Heading as="h2" size="xsmall">
						{heading}
					</Heading>
				) : null}
				{imageButtons}
				{viewer}
			</Stack>
		);
	}

	return (
		<Fragment>
			<Carousel heading={heading}>{imageButtons}</Carousel>
			{viewer}
		</Fragment>
	);
};
